import React, { useCallback, useMemo, useState } from 'react';
import { Marker as MarkerL, Popup } from 'react-leaflet';
import * as ReactDOMServer from 'react-dom/server';
import L from 'leaflet';
import { LuTreePine } from 'react-icons/lu';
import { CreatePoiPayload, NodePoint, Place } from '../../types/types';
import configService from '../../services/configService';
import enkarturPois from '../../../data/enkartur/poi_enkartur.json';
import './nodePointMarker.css';

type EnkarturPoiRecord = {
  title?: string;
  [key: string]: unknown;
};

const ENKARTUR_TENANT_ID = 'enkartur';
const ENKARTUR_IMAGES_BASE_URL = 'https://www.visitenkarterri.com';
const ENKARTUR_IMAGES_FIELD = 'listado imagenes';

const normalizeTitle = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getFirstRealImagePath = (rawValue: unknown): string | null => {
  if (typeof rawValue !== 'string') {
    return null;
  }

  const firstRealImage = rawValue
    .split('\n')
    .map((entry) => entry.trim())
    .find((entry) => entry.includes('Real'));

  if (!firstRealImage) {
    return null;
  }

  return firstRealImage.startsWith('/') ? firstRealImage : `/${firstRealImage}`;
};

const enkarturImageByTitle = (enkarturPois as EnkarturPoiRecord[]).reduce<Record<string, string>>((acc, poi) => {
  if (typeof poi.title !== 'string') {
    return acc;
  }

  const imagePath = getFirstRealImagePath(poi[ENKARTUR_IMAGES_FIELD]);
  if (!imagePath) {
    return acc;
  }

  acc[normalizeTitle(poi.title)] = `${ENKARTUR_IMAGES_BASE_URL}${imagePath}`;
  return acc;
}, {});

interface NodePointMarkerProps {
  node?: NodePoint;
  place?: Place;
  currentZoom: number;
  isMapClicked: boolean;
  isAdmin?: boolean;
  onCreatePoi?: (payload: CreatePoiPayload) => Promise<void>;
}

export const NodePointMarker: React.FC<NodePointMarkerProps> = ({
  node,
  place,
  currentZoom,
  isMapClicked,
  isAdmin = false,
  onCreatePoi,
}) => {
  const hasPlace = Boolean(place);
  const position: [number, number] = hasPlace
    ? [place!.location.latitude, place!.location.longitude]
    : [node!.lat, node!.lon];

  const title = node ? node.title : place?.displayName.text ?? 'Punto de interés';
  const description = node?.description ?? place?.editorialSummary?.text ?? null;

  // Create appropriate icon based on type
  const icon = L.divIcon({
    className: 'marker-with-label',
    popupAnchor: [0, -10],
    html: ReactDOMServer.renderToString(
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div className='marker'>
          <LuTreePine />
        </div>
        <span className= "icon-title">{title}</span>
      </div>
    ),
  })

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const tenantId = configService.getConfig().tenantId;

  const imageUrl = useMemo(() => {
    if (node?.url) {
      return node.url;
    }

    if (tenantId === ENKARTUR_TENANT_ID && node?.title) {
      return enkarturImageByTitle[normalizeTitle(node.title)] ?? null;
    }

    const photoName = place?.photos?.[0]?.name;
    if (!photoName) {
      return null;
    }

    return `https://7gfyuakvzdypdtw7hhyntba4ha0tagdz.lambda-url.eu-west-1.on.aws/?photoName=${photoName}&maxWidthPx=320`;
  }, [node, place, tenantId]);

  const handleCreate = useCallback(async () => {
    if (!onCreatePoi || !isAdmin || !hasPlace) {
      return;
    }

    const payload: CreatePoiPayload = {
      title,
      lat: place!.location.latitude,
      lon: place!.location.longitude,
      short_description: description ?? null,
      image_url: imageUrl,
    };

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await onCreatePoi(payload);
      setFeedback('POI creado correctamente.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudo crear el POI';
      setFeedback(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [onCreatePoi, isAdmin, hasPlace, title, description, imageUrl, place]);

  return (
    <MarkerL
      position={position}
      icon={icon}
      eventHandlers={{
        click: (event) => {
          event.target.openPopup();
        }
      }}
    >
      <Popup
        keepInView={false}
        autoPan={false}
        closeButton={false}
      >
        <div className="popup-container">
          {imageUrl && (
            <img
              className="popup-image"
              src={imageUrl}
              alt={title}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          
          <div className="popup-content">
            <h3 className="popup-title">
              {title}
              {isAdmin && hasPlace && (
                <span className="popup-title-emoji" role="img" aria-label="Seleccionar">
                  ✅
                </span>
              )}
            </h3>

            {place?.primaryType && (
              <div className="popup-type">
                {place.primaryType.replace(/_/g, ' ')}
              </div>
            )}

            {description && (
              <p className="popup-description">
                {description}
              </p>
            )}

            {isAdmin && hasPlace && onCreatePoi && (
              <button
                type="button"
                className="poi-create-button"
                onClick={handleCreate}
                disabled={isSubmitting}
              >
                ✅
              </button>
            )}

            {feedback && (
              <div className={`poi-feedback ${feedback.includes('correctamente') ? 'success' : 'error'}`}>
                {feedback}
              </div>
            )}
          </div>
        </div>
      </Popup>
    </MarkerL>
  );
};
